import React from "react"
import { Box, Button, Typography } from "@material-ui/core"
import ThemeProvider from "@material-ui/styles/ThemeProvider"
import Layout from "../../components/layout"
import SEO from "../../components/seo"
import ArticleHero from "../../components/article-hero"
import theme from "../../theme"
import { articleArtwork } from "../../data/article-artwork-data"

const title = "School Holiday Screen Time: A Routine for Every Age"
const description =
  "Make room for games, movement, friends and rest with a practical holiday routine for children aged 5–8, 9–12 and 13–17."
const artwork = articleArtwork["school-holiday-screen-time"]
const screenAdvice =
  "https://www.esafety.gov.au/parents/issues-and-advice/screen-time"
const familyPlan =
  "https://www.healthychildren.org/English/family-life/Media/Pages/How-to-Make-a-Family-Media-Use-Plan.aspx"

const Section = ({
  id,
  heading,
  children,
}: {
  id: string
  heading: string
  children: React.ReactNode
}) => (
  <Box component="section" id={id} style={{ marginTop: 32 }}>
    <Typography variant="h5" component="h2" gutterBottom>
      {heading}
    </Typography>
    {children}
  </Box>
)
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography paragraph>{children}</Typography>
)

export default function HolidayScreenTimePage() {
  return (
    <ThemeProvider theme={theme}>
      <Layout>
        <SEO title={title} description={description} />
        <Box
          component="article"
          style={{ maxWidth: 960, margin: "0 auto", padding: "40px 0 56px" }}
        >
          <Button href="/guides/?group=Use%20cases" color="primary">
            Back to family routines
          </Button>
          <ArticleHero
            title={title}
            intro="When games and videos fill the school holidays, a few predictable boundaries can help make space for the rest of the day. Start with a manageable family routine and adjust the support for each child’s age."
            category="Use cases · Family routines"
            image={artwork.image}
            imageAlt={artwork.alt}
          />
          <Box
            style={{
              maxWidth: 800,
              margin: "0 auto",
              fontFamily: theme.typography.fontFamily,
            }}
          >
            <P>
              Holidays leave more unstructured time, and screens can quickly
              become the default. A child may resist a suggestion to go outside
              yet happily leave for a planned visit with friends. Use that as a
              starting point: make the next activity concrete, and make screen
              time predictable.
            </P>
            <P>
              This guide is for school-aged children and teenagers. The age
              bands are starting points; adapt them to maturity, disability,
              sensory needs and family circumstances. The sample routine below
              is an editorial example, not a prescribed screen-time allowance.
            </P>
            <P>
              <strong>Where should a family start?</strong> Agree on a small
              number of predictable screen windows and one manageable offline
              activity. Help younger children with the transition, involve
              tweens in planning, and negotiate more independence with
              teenagers. Review the arrangement after several days.
            </P>
            <P>
              <a href="/downloads/holiday-family-agreement.html">
                Fill in or print a family agreement
              </a>
              . Choose your own timings and support for each child; the
              worksheet does not prescribe a daily allowance.
            </P>
            <nav aria-label="On this page">
              <a href="#balance">Check the balance</a> ·{" "}
              <a href="#ages">Approaches by age</a> ·{" "}
              <a href="#boundaries">Set boundaries</a> ·{" "}
              <a href="#example">Example day</a> ·{" "}
              <a href="#review">Review the week</a>
            </nav>

            <Section
              id="balance"
              heading="Look at what screens are crowding out"
            >
              <P>
                Consider sleep, meals, movement, relationships and other
                interests alongside time online. Notice the content and how your
                child feels during and after using it. Wanting another game is
                not, on its own, evidence of addiction. If gaming occupies most
                waking hours, it is reasonable to introduce more structure.{" "}
                <a href={screenAdvice}>eSafety’s screen-time guidance</a> helps
                parents assess the impact on everyday life.
              </P>
              <P>
                Ask to see a favourite game or video before discussing changes.
                “What do you enjoy about this?” is a useful opener. Playing with
                friends, making something and watching an endless feed may serve
                different purposes; find out what matters to your child.
              </P>
            </Section>

            <Section
              id="ages"
              heading="Use different levels of support at different ages"
            >
              <Typography variant="h6" component="h3" gutterBottom>
                Ages 5–8: lead the transition
              </Typography>
              <P>
                Set the schedule and offer two specific alternatives: “After
                this episode, shall we build a den or take the ball outside?”
                Stay nearby at stopping time and help the next activity get
                started. A simple picture schedule can show play, lunch, screen
                time and the next outing. Choose content together and supervise
                use.
              </P>
              <Typography variant="h6" component="h3" gutterBottom>
                Ages 9–12: plan together within clear limits
              </Typography>
              <P>
                Let children help choose gaming windows and offline activities.
                Agree on the finishing time before they start, and ask how long
                a typical round takes. They could arrange a friend visit with
                your help, choose a recipe, or plan a short bike ride. Review
                purchases, contacts and content settings together; explain what
                parental controls will do.
              </P>
              <Typography variant="h6" component="h3" gutterBottom>
                Ages 13–17: negotiate responsibility and independence
              </Typography>
              <P>
                Discuss the teenager’s own plans, including when friends are
                online. Agree on boundaries around sleep, meals and commitments,
                then give them room to propose how their day will work. A walk
                with a friend may appeal more than a family park visit. Make
                check-ins transparent and brief; avoid requiring the same
                outings or schedule as a younger sibling.
              </P>
              <P>
                These are practical examples of increasing participation, not
                fixed developmental rules. The{" "}
                <a href={familyPlan}>
                  American Academy of Pediatrics’ family media plan
                </a>{" "}
                encourages family discussion and rules that can change as
                children grow.
              </P>
            </Section>

            <Section
              id="boundaries"
              heading="Make a small agreement you can keep"
            >
              <P>
                Discuss the change when nobody is in the middle of a game. Try:
                “We want the holidays to include gaming, friends, rest and time
                away from screens. Let’s choose when each fits, so we don’t have
                to argue about it all day.”
              </P>
              <P>
                Start with a few predictable rules: screen-free meals, a
                screen-free wind-down before bed, and agreed gaming or video
                windows. Adults should follow shared rules too. Device settings
                can support an agreement, but explain them before activating a
                cutoff. These measures reflect the{" "}
                <a href={familyPlan}>AAP’s family media planning advice</a>.
              </P>
              <P>
                Write down when play starts, when it ends and when the next
                opportunity will be. Give an advance warning and account for a
                round that cannot be paused. “There is time for one more short
                round; don’t start another after that” is clearer than
                repeatedly saying “soon”.
              </P>
              <P>
                If a child protests, acknowledge the disappointment and keep the
                agreed boundary calmly: “I know you wanted to continue. We’re
                stopping now; your next session is this afternoon.” Avoid
                inventing a bigger punishment in the argument. If stopping
                repeatedly goes badly, revisit the timing and support when
                everyone is calm.
              </P>
            </Section>

            <Section
              id="reluctance"
              heading="When “go outside” gets a reluctant response"
            >
              <P>
                Offer a plan with a beginning and an end: “We’re going for a
                short walk after breakfast. Would you like to choose the route?”
                For younger children, go with them. For teenagers, ask them to
                suggest a realistic alternative that still meets the agreement.
              </P>
              <P>
                Make movement enjoyable and accessible: a playground, dancing,
                swimming, wheeling or walking, or a casual ball game. Ask what
                makes an activity unappealing—heat, noise, difficulty or going
                alone may matter. A gym or paid class is not essential.
              </P>
              <P>
                Keep indoor options available too: comics, drawing, building,
                music or cooking. Families with work commitments can plan one
                shared activity and leave independent choices for other periods.
                Children do not need constant entertainment, and every offline
                activity does not need to be educational.
              </P>
            </Section>

            <Section id="example" heading="Try a flexible holiday day">
              <P>
                Choose times that fit your household. The sequence below
                illustrates a rhythm; it does not specify a daily screen-time
                limit or require every family to use two gaming windows.
              </P>
              <Box
                component="ol"
                style={{
                  paddingLeft: 24,
                  lineHeight: 1.8,
                  background: "#f3f7ff",
                  borderRadius: 12,
                  padding: "20px 24px 20px 40px",
                }}
              >
                <li>
                  <strong>Morning:</strong> breakfast, getting ready and a small
                  household task.
                </li>
                <li>
                  <strong>A shared start:</strong> a short outing or an
                  accessible activity chosen together.
                </li>
                <li>
                  <strong>Agreed screen window:</strong> gaming or videos with a
                  known finishing time.
                </li>
                <li>
                  <strong>Lunch and offline downtime:</strong> food, rest,
                  reading, building or another choice.
                </li>
                <li>
                  <strong>Afternoon:</strong> friends, an activity or
                  independent time; another screen window only if it is part of
                  the agreement.
                </li>
                <li>
                  <strong>Evening:</strong> a meal together and a predictable
                  wind-down, with devices put away for sleep.
                </li>
              </Box>
              <P>
                With siblings, keep shared meals or an outing as common anchors
                and allow different activities around them. A teenager’s social
                gaming session need not begin when a younger child’s video time
                begins.
              </P>
              <P>
                Put just the agreed anchors and screen windows into a calendar.
                Leave gaps for rest and changes of plan. You can use paper or{" "}
                <a href="/how-to-guides/generate-calendar-events-ai/">
                  draft the agreed events with Magic Generate
                </a>
                ; review the dates, times and durations before adding them. A
                calendar makes the agreement visible, but does not enforce
                device limits.
              </P>
            </Section>

            <Section id="review" heading="Review the week, not every minute">
              <P>
                After several days, ask: Which stopping time worked? Was there
                an activity worth repeating? Did everyone get enough rest and
                time together? Keep what helped and change one sticking point.
                Early success may be following the routine with support rather
                than being enthusiastic about every transition.
              </P>
              <P>
                If screen use continues to interfere substantially with sleep,
                relationships or everyday functioning despite support, speak
                with a GP or another qualified health professional. Look at the
                wider situation rather than assigning an addiction label from
                hours alone. See{" "}
                <a href={screenAdvice}>
                  eSafety’s advice on recognising problems and getting support
                </a>
                .
              </P>
            </Section>

            <Section id="related" heading="Plan the next small step">
              <P>
                For tomorrow, choose one shared activity and one clear stopping
                time. For more ideas, explore{" "}
                <a href="/trends/school-holiday-activities/">
                  school holiday activities
                </a>{" "}
                or build an{" "}
                <a href="/use-cases/after-school-activity-schedule-ai/">
                  after-school routine
                </a>{" "}
                when term returns.
              </P>
            </Section>
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 32 }}
            >
              Reviewed 28 September 2026. General family guidance; the sample
              routine and conversation examples are editorial suggestions.
              Sources: eSafety Commissioner and American Academy of Pediatrics,
              linked alongside the relevant advice.
            </Typography>
          </Box>
        </Box>
      </Layout>
    </ThemeProvider>
  )
}
